import { useState } from "react";
import { useMsal } from "@azure/msal-react";
import {
  LayoutDashboard,
  Target,
  Route,
  Network,
  ClipboardList,
} from "lucide-react";
import { getUserRole } from "../authConfig";
import FinancialSnapshotView from "./FinancialSnapshotView";
import SmsfPlanView from "./SmsfPlanView";
import HowItWorksView from "./HowItWorksView";
import SystemMapView from "./SystemMapView";
import ListsView from "./ListsView";

const tabs = [
  { id: "home", label: "Home", Icon: LayoutDashboard },
  { id: "smsf", label: "SMSF", Icon: Target },
  { id: "how", label: "How", Icon: Route },
  { id: "map", label: "Map", Icon: Network },
  { id: "lists", label: "Lists", Icon: ClipboardList },
];

const NAVY = "#1B3A5C";
const STEEL = "#4A6D8C";

function TabBar({ activeView, setActiveView }) {
  return (
    <nav className="fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 z-20">
      <div
        className="grid grid-cols-5 max-w-3xl mx-auto"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        {tabs.map((tab) => {
          const active = activeView === tab.id;
          const Icon = tab.Icon;
          const color = active ? NAVY : STEEL;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveView(tab.id)}
              className="flex flex-col items-center justify-center py-2 transition-colors"
              style={{ color }}
              aria-current={active ? "page" : undefined}
              aria-label={tab.label}
            >
              <Icon
                size={24}
                strokeWidth={active ? 2.25 : 1.75}
                fill={active ? "rgba(27, 58, 92, 0.12)" : "none"}
              />
              <span
                className={`text-[11px] mt-1 leading-none ${
                  active ? "font-semibold" : "font-medium"
                }`}
              >
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

export default function Dashboard() {
  const { instance, accounts } = useMsal();
  const account = accounts[0];
  const role = getUserRole(account);
  const displayName = account?.name || account?.username || "User";
  const isAdmin = role === "admin";

  const [activeView, setActiveView] = useState("lists");

  function handleLogout() {
    instance.logoutRedirect();
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navy header strip */}
      <header className="bg-[#1B3A5C] text-white sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="min-w-0">
            <h1 className="text-sm font-semibold tracking-wide truncate">
              Sandeu DLS
            </h1>
            <p className="text-[11px] text-slate-300 truncate">
              {displayName}
              {isAdmin && (
                <span className="ml-2 bg-white/15 text-white px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider">
                  Admin
                </span>
              )}
            </p>
          </div>
          <button
            onClick={handleLogout}
            className="text-xs text-slate-300 hover:text-white px-3 py-1.5 rounded-lg hover:bg-white/10 transition-colors shrink-0"
          >
            Sign out
          </button>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-3xl mx-auto px-4 py-6 pb-24">
        {activeView === "home" && <FinancialSnapshotView />}
        {activeView === "smsf" && <SmsfPlanView />}
        {activeView === "how" && <HowItWorksView />}
        {activeView === "map" && <SystemMapView />}
        {activeView === "lists" && <ListsView />}
      </main>

      <TabBar activeView={activeView} setActiveView={setActiveView} />
    </div>
  );
}
